import { Button, Flex, Label, ModalManagerContext, Text, Grid, TabPanel, Banner } from '@pega/cosmos-react-core';
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useLocalTranslation from '../../../hooks/useLocalTranslation';
import SectionRenderTemplate from '../../Utilities/SectionRenderTemplate';
import { tokenMaskerStyle } from '../../Utilities/custom.styled';
import { appState, confirmationAlertProps } from '../../../interfaces/common';
import { updateBannerInfo, updateLoaderDetails } from '../../../redux/actions';
import { getAccountDetails, deleteAccount } from './ThirdPartyConnectionApis';
import { ModalMethods } from '@pega/cosmos-react-core/lib/components/Modal/Modal.types';
import { ConfirmationAlert } from '../../Utilities/CustomModal';
import { usePlatformVersionSelector } from '../../../redux/selectors';
import SimulatorConfiguration from '../CustomerAISimulator/Tabs/SimulatorConfiguration';
import CustomerAISimulatorConnection from '../CustomerAISimulator/Tabs/CustomerAISimulatorConnection';
import { StyledTabs } from '../../Utilities/custom.styled';
import LiveSimulation from '../CustomerAISimulator/Tabs/LiveSimulation';
import { AgentTrainerTabNames } from '../../../constants/common';
import SimulationMetrics from '../CustomerAISimulator/Tabs/SimulationMetrics';
import Personas from '../CustomerAISimulator/Tabs/Personas';
import AgentFeedback from '../CustomerAISimulator/Tabs/AgentFeedback';

interface APIFormData {
  accountName: string;
  webhook: string;
  jwtToken: string;
  isCustomerSimulator: boolean;
  apiKey: string;
  simulationStatus: string;
  customerData?: any;
  validConnection?: boolean;
  host?: string;
  customerUploadTemplateUrl?: string;
}

const ApiConnection: React.FC<any> = (props) => {
  const location = useLocation();
  const { id: connectionId }: any = useParams();
  const { sessionId, enableAgentTrainer, genAiKeyOption } = useSelector((state: appState) => state);
  const initialState: APIFormData = {
    accountName: '',
    webhook: '',
    jwtToken: '',
    apiKey: '',
    isCustomerSimulator: false,
    simulationStatus: '',
    customerData: [],
  };
  const [accountDetails, setAccountDetails] = useState(initialState);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { majorVersion, minorVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  const modalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const [panelShown, changePanel] = useState('0');
  const socketRef = useRef<WebSocket | undefined>(undefined);

  const fetchAccountDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getAccountDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      setAccountDetails(response?.data);
    } else {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId, navigate]);

  useEffect(() => {
    if (location.pathname === '/custom/:id' && !beyondEightDotSeven) {
      navigate('/');
      return;
    }
    fetchAccountDetails();
  }, [fetchAccountDetails, location.pathname, beyondEightDotSeven, navigate]);

  useEffect(() => {
    if (location.pathname === '/custom/:id' && accountDetails.isCustomerSimulator && !enableAgentTrainer) {
      navigate('/');
      return;
    }
  }, [accountDetails, location.pathname, enableAgentTrainer, navigate]);

  const headerElement = () => {
    return (
      <>
        {accountDetails?.accountName && <Text variant="h1">{accountDetails?.accountName}</Text>}
        {accountDetails?.isCustomerSimulator && genAiKeyOption !== 'pega' && !accountDetails.host && (
          <Banner
            id="genAI_info_banner"
            variant="info"
            messages={[translate('genAI_info_banner')]}
            style={{ paddingTop: '1rem' }}
          />
        )}
      </>
    );
  };

  const [isMaskEnabled, setIsMaskEnabled] = useState(true);

  const getMaskedToken = (token: string = '') => {
    return !isMaskEnabled ? token : new Array(token.length).fill('*').join('');
  };

  const { create } = useContext(ModalManagerContext);
  const deleteConfirmation = () => {
    modalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: accountDetails?.accountName,
        deleteChannel: deleteChannel,
        ...props,
      },
      { alert: true },
    );
  };

  const deleteChannel = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await deleteAccount(connectionId, sessionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      navigate('/');
      dispatch(
        updateBannerInfo(
          `${translate(accountDetails.isCustomerSimulator ? 'GenAI Agent Trainer' : 'third_party')} ${translate(
            'account_deleted',
          )}`,
          'success',
        ),
      );
    } else if (response?.status === 403) {
      navigate('/');
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    } else {
      dispatch(updateBannerInfo(response?.errorMessage ? translate(response?.errorMessage) : '', 'urgent'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, connectionId, dispatch, navigate, translate]);

  const closeSocketConnection = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }
  };

  const getBaseTabs = useCallback(() => {
    const arr = [
      AgentTrainerTabNames.SimulationConfiguration,
      AgentTrainerTabNames.LiveSimulation,
      AgentTrainerTabNames.SimulationHistory,
      AgentTrainerTabNames.ConnectionAndGenAI,
      AgentTrainerTabNames.Personas,
      AgentTrainerTabNames.AgentFeedback,
    ];
    return arr.map((tabName, index) => ({
      name: translate(tabName),
      id: index.toString(),
      disabled: tabName === 'Live simulation' && accountDetails?.simulationStatus !== 'start' ? true : false,
      label: tabName,
    }));
  }, [translate, accountDetails]);

  const newTabs = getBaseTabs();

  const renderTabContent = (tabName: string) => {
    switch (tabName) {
      case AgentTrainerTabNames.SimulationConfiguration:
        return (
          <SimulatorConfiguration
            connectionId={connectionId}
            accountDetails={accountDetails}
            setAccountDetails={setAccountDetails}
          />
        );
      case AgentTrainerTabNames.ConnectionAndGenAI:
        return (
          <CustomerAISimulatorConnection
            connectionId={connectionId}
            accountDetails={accountDetails}
            setAccountDetails={setAccountDetails}
            sessionId={sessionId}
            deleteConfirmation={deleteConfirmation}
            genAiKeyOption={genAiKeyOption}
          />
        );
      case AgentTrainerTabNames.LiveSimulation:
        return <LiveSimulation connectionId={connectionId} socketRef={socketRef} />;
      case AgentTrainerTabNames.SimulationHistory:
        return <SimulationMetrics connectionId={connectionId} sessionId={sessionId} />;
      case AgentTrainerTabNames.Personas:
        return (
          <Personas
            simulationStatus={accountDetails?.simulationStatus}
            connectionId={connectionId}
            customerData={accountDetails?.customerData}
            customerUploadTemplateUrl={accountDetails?.customerUploadTemplateUrl}
            setAccountDetails={setAccountDetails}
          />
        );
      case AgentTrainerTabNames.AgentFeedback:
        return (
          <AgentFeedback
            connectionId={connectionId}
            accountDetails={accountDetails}
            setAccountDetails={setAccountDetails}
          />
        );
      default:
        return '';
    }
  };

  const handleTabChange = (id: string) => {
    changePanel(id);
  };

  useEffect(() => {
    if (parseInt(panelShown) !== 1) {
      closeSocketConnection();
    }
  }, [panelShown]);

  return (
    <SectionRenderTemplate header={headerElement()}>
      {accountDetails?.accountName && (
        <>
          {!accountDetails.isCustomerSimulator ? (
            <>
              <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
                <Grid
                  container={{
                    cols: 'repeat(2, 33% 67%)',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Label>{translate('connection_id')}</Label>
                  <Text variant="secondary">{connectionId ? connectionId.replace('custom_', '') : ''}</Text>
                </Grid>
                <Grid
                  container={{
                    cols: 'repeat(2, 33% 67%)',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Label>{translate('client_Webhook')}</Label>
                  <Text variant="secondary">{accountDetails?.webhook}</Text>
                </Grid>
                <Grid
                  container={{
                    cols: 'repeat(2, 33% 67%)',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Label>{translate('digital_messaging_webhook')}</Label>
                  <Text variant="secondary">{process.env.REACT_APP_INCOMING_WEBHOOK}/messages</Text>
                </Grid>
                <Grid
                  container={{
                    cols: 'repeat(2, 33% 67%)',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <Label>{translate('jwt_secret_label')}</Label>
                  <Flex container={{ itemGap: 2, alignItems: 'end' }}>
                    <Text variant="secondary">{getMaskedToken(accountDetails?.jwtToken)}</Text>
                    <Button style={tokenMaskerStyle} onClick={() => setIsMaskEnabled(!isMaskEnabled)}>
                      {translate(isMaskEnabled ? 'show_token' : 'hide_token')}
                    </Button>
                  </Flex>
                </Grid>
                <Text variant="secondary">{translate('jwt_secret_description_3rd_party')}</Text>
              </Flex>
              <Flex style={{ paddingTop: '0.5rem' }}>
                <Button onClick={deleteConfirmation}>{translate('delete_connection_button')}</Button>
              </Flex>
            </>
          ) : (
            <>
              <StyledTabs tabs={newTabs} type="horizontal" onTabClick={handleTabChange} currentTabId={panelShown} />
              <TabPanel tabId={panelShown}>{renderTabContent(newTabs[parseInt(panelShown, 10)].label)}</TabPanel>
            </>
          )}
        </>
      )}
    </SectionRenderTemplate>
  );
};

export default ApiConnection;
